// Cross-Skill, redesigned (replaces the two-column chain diagram). A 2-zone Advanced-ON row:
//   [ What moved the score — Ranked / Cards toggle ] [ The Domino ]
// The Final Score cluster was removed from here (it already lives in the right-hand rail) to give the
// diagram + domino the full width. The old node-link chains needed a legend + hover to read; this is a
// single ranked list (or grouped chips) plus the ONE root-cause "domino" pulled into its own frame.
// Everything is derived on the frontend from the scored result — no backend change.

import type { AggregateResult, HighLevelGraph, SkillBreakdownRow } from '../lib/types.js';
import { displaySkillName } from '../lib/skills.js';

type View = 'ranked' | 'cards';
const VIEW_KEY = 'studio.crossView.v1';

function getView(): View {
  try {
    return localStorage.getItem(VIEW_KEY) === 'cards' ? 'cards' : 'ranked';
  } catch {
    return 'ranked';
  }
}
function setView(v: View): void {
  try {
    localStorage.setItem(VIEW_KEY, v);
  } catch {
    /* private mode */
  }
}

function el(tag: string, cls?: string, text?: string): HTMLElement {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text != null) n.textContent = text;
  return n;
}

// ---- data prep ------------------------------------------------------------------------------------

interface Row {
  name: string;
  actual: number | null; // null = not scored
  expected: number;
  gap: number; // rounded actual - expected (0 when not scored)
  critical: boolean;
  scored: boolean;
}

function toRows(r: AggregateResult): Row[] {
  return r.skill_breakdown.map((s: SkillBreakdownRow): Row => {
    const scored = s.evidence_state === 'scored' && s.actual_score != null;
    const actual = scored ? (s.actual_score as number) : null;
    const gap = scored ? Math.round((actual as number) - s.expected_score) : 0;
    return { name: displaySkillName(s.skill_name), actual, expected: s.expected_score, gap, critical: s.critical, scored };
  });
}

/** Scored skills first (biggest drag → smallest), then not-scored. */
function rankedSort(rows: Row[]): Row[] {
  return [...rows].sort((a, b) => {
    if (a.scored !== b.scored) return a.scored ? -1 : 1;
    return a.gap - b.gap;
  });
}

interface Domino {
  rootName: string;
  actual: number | null;
  expected: number;
  critical: boolean;
  note: string;
  downstream: string[];
}

function toDomino(r: AggregateResult): Domino | null {
  const drag: HighLevelGraph | undefined = r.high_level_summary?.graphs?.find((g) => g.kind === 'drag');
  if (!drag || !drag.root) return null;
  const rootNode = drag.nodes.find((n) => n.id === drag.root);
  if (!rootNode) return null;
  const rootRow = r.skill_breakdown.find((s) => s.skill_id === drag.root);
  const nameOf = (id: string): string => {
    const n = drag.nodes.find((nd) => nd.id === id)?.name;
    return n ? displaySkillName(n) : '';
  };
  let downstream = drag.edges.filter((e) => e.from === drag.root).map((e) => nameOf(e.to)).filter(Boolean);
  if (!downstream.length) downstream = drag.nodes.filter((n) => n.id !== drag.root).map((n) => displaySkillName(n.name));
  return {
    rootName: displaySkillName(rootNode.name),
    actual: rootRow?.actual_score ?? null,
    expected: rootRow?.expected_score ?? 0,
    critical: rootRow?.critical ?? false,
    note: rootNode.note ?? '',
    downstream,
  };
}

// ---- zone 2a: ranked impact bars ------------------------------------------------------------------

function renderRanked(host: HTMLElement, rows: Row[]): void {
  const sorted = rankedSort(rows);
  const maxGap = Math.max(1, ...sorted.filter((r) => r.scored).map((r) => Math.abs(r.gap)));
  // Bar length is a share of the track (each half-track = the biggest gap), so a bar can never overflow
  // its column into the domino no matter how narrow the diagram gets.
  const pct = (g: number): number => (Math.abs(g) / maxGap) * 50;

  const axis = el('div', 'czb-axis');
  axis.append(el('span', '', ''), el('span', 'mid', '◀ below · target · at / above ▶'), el('span', '', ''));
  host.appendChild(axis);

  for (const row of sorted) {
    const r = el('div', 'czb-row' + (row.scored ? '' : ' na'));
    const lbl = el('div', 'czb-lbl');
    // Name truncates on its own; the CRIT badge lives in a non-shrinking sibling so a long name can
    // never clip it (that clipping made ranked under-report criticals vs. Cards — and made the visible
    // set flip whenever a skill's display name changed length).
    lbl.appendChild(el('span', 'czb-nm', row.name));
    if (row.critical) lbl.appendChild(el('span', 'czb-crit', 'crit'));
    const track = el('div', 'czb-track');
    if (!row.scored) {
      track.appendChild(el('span', 'czb-na-txt', 'not scored'));
    } else if (row.gap < 0) {
      const bar = el('div', 'czb-bar red');
      bar.style.width = pct(row.gap) + '%';
      track.appendChild(bar);
    } else if (row.gap > 0) {
      const bar = el('div', 'czb-bar green');
      bar.style.width = pct(row.gap) + '%';
      track.appendChild(bar);
    } else {
      track.appendChild(el('span', 'czb-met')); // exactly on target
    }
    const val = el('div', 'czb-val');
    if (row.scored) {
      val.appendChild(el('b', '', String(row.actual)));
      val.appendChild(document.createTextNode('/' + row.expected));
      if (row.gap !== 0) val.appendChild(el('span', row.gap < 0 ? 'down' : 'up', ' ' + (row.gap > 0 ? '+' : '−') + Math.abs(row.gap)));
      else val.appendChild(document.createTextNode(' met'));
    } else {
      val.textContent = '—';
    }
    r.append(lbl, track, val);
    host.appendChild(r);
  }

  const legend = el('div', 'czb-legend');
  const item = (bg: string, label: string): HTMLElement => {
    const s = el('span');
    const sw = el('i', 'sw');
    sw.style.background = bg;
    s.append(sw, document.createTextNode(label));
    return s;
  };
  legend.append(item('#ff7a6e', 'below target'), item('#54f0a0', 'at / above')); // HUD semantic red/green (cross-skill is dark)
  const crit = el('span');
  crit.append(el('span', 'czb-crit', 'crit'), document.createTextNode(' critical (weighs most)'));
  legend.appendChild(crit);
  host.appendChild(legend);
}

// ---- zone 2b: story cards (grouped chips) ---------------------------------------------------------

function chip(name: string, actual: number | null, kind: 'red' | 'green', critical: boolean): HTMLElement {
  const c = el('span', 'czc-chip ' + kind);
  c.appendChild(document.createTextNode(name + ' '));
  c.appendChild(el('b', '', actual == null ? '—' : String(actual)));
  if (critical) c.appendChild(el('span', 'czc-crit', 'crit'));
  return c;
}

function renderCards(host: HTMLElement, rows: Row[]): void {
  const dragged = rankedSort(rows.filter((r) => r.scored && r.gap < 0));
  const lifted = rows.filter((r) => r.scored && r.gap >= 0).sort((a, b) => b.gap - a.gap);
  const notScored = rows.filter((r) => !r.scored);

  const group = (label: string, color: string, items: Row[], kind: 'red' | 'green', empty: string): HTMLElement => {
    const g = el('div', 'czc-grp');
    const lbl = el('div', 'czc-lbl');
    lbl.style.color = color;
    const dot = el('span', 'czc-dot');
    dot.style.background = color;
    lbl.append(dot, document.createTextNode(label));
    const chips = el('div', 'czc-chips');
    if (items.length) for (const it of items) chips.appendChild(chip(it.name, it.actual, kind, it.critical));
    else chips.appendChild(el('span', 'czc-empty', empty));
    g.append(lbl, chips);
    return g;
  };

  host.appendChild(group('Dragged', '#ff7a6e', dragged, 'red', 'Nothing dragged the call.'));
  host.appendChild(group('Held up', '#54f0a0', lifted, 'green', 'Nothing cleared the bar.'));
  if (notScored.length) {
    host.appendChild(el('p', 'czc-foot', notScored.map((r) => r.name).join(', ') + ' — not scored on this call.'));
  }
}

// ---- zone 3: the domino ---------------------------------------------------------------------------

function renderDomino(host: HTMLElement, d: Domino | null): void {
  const frame = el('div', 'czd-frame');
  frame.appendChild(el('div', 'czd-k', '🁢 The domino · fix this first'));
  const body = el('p', 'czd-body');
  if (!d) {
    body.textContent = 'No single blocker on this call — the gaps here are independent. Work the biggest drags in the middle, top-down.';
  } else {
    const meta = d.actual == null ? '' : ` (${d.critical ? 'critical, ' : ''}scored ${d.actual} of ${d.expected})`;
    body.appendChild(el('b', '', d.rootName));
    body.appendChild(document.createTextNode(`${meta} is the root cause. `));
    if (d.note) body.appendChild(document.createTextNode(d.note.replace(/\s*$/, '') + ' '));
    if (d.downstream.length) {
      body.appendChild(document.createTextNode('Fixing it first lets '));
      d.downstream.forEach((name, i) => {
        if (i > 0) body.appendChild(document.createTextNode(i === d.downstream.length - 1 ? ' and ' : ', '));
        body.appendChild(el('b', '', name));
      });
      body.appendChild(document.createTextNode(' land.'));
    }
  }
  frame.appendChild(body);
  host.appendChild(frame);
}

// ---- zone assembly + toggle -----------------------------------------------------------------------

function paintDiagram(container: HTMLElement, rows: Row[]): void {
  container.innerHTML = '';
  if (getView() === 'cards') renderCards(container, rows);
  else renderRanked(container, rows);
  syncToggle();
}

function syncToggle(): void {
  const v = getView();
  const ranked = document.getElementById('cross-view-ranked');
  const cards = document.getElementById('cross-view-cards');
  ranked?.classList.toggle('is-on', v === 'ranked');
  cards?.classList.toggle('is-on', v === 'cards');
  ranked?.setAttribute('aria-selected', String(v === 'ranked'));
  cards?.setAttribute('aria-selected', String(v === 'cards'));
}

/** Build the 3-zone Cross-Skill layout into `host` (which sits inside the cross-skill view). */
export function renderCrossSkillZones(host: HTMLElement, r: AggregateResult): void {
  host.innerHTML = '';
  const rows = toRows(r);
  const domino = toDomino(r);

  const zones = el('div', 'cs-zones');

  // Zone 1 — What moved the score, with the Ranked / Cards toggle. (The Final Score cluster that used to
  // sit to the left of this was removed — it duplicates the right-hand rail — freeing the width.)
  const zDia = el('section', 'cs-zone cs-zone-diagram');
  const head = el('div', 'cs-zone-head');
  head.appendChild(el('h3', 'cs-zone-h', 'What moved the score'));
  const toggle = el('div', 'czt-toggle');
  toggle.setAttribute('role', 'tablist');
  const mkBtn = (id: string, label: string): HTMLButtonElement => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'czt-btn';
    b.id = id;
    b.setAttribute('role', 'tab');
    b.textContent = label;
    return b;
  };
  const bRanked = mkBtn('cross-view-ranked', 'Ranked');
  const bCards = mkBtn('cross-view-cards', 'Cards');
  toggle.append(bRanked, bCards);
  head.appendChild(toggle);
  zDia.appendChild(head);
  const diagram = el('div', 'czt-body');
  zDia.appendChild(diagram);
  bRanked.addEventListener('click', () => {
    setView('ranked');
    paintDiagram(diagram, rows);
  });
  bCards.addEventListener('click', () => {
    setView('cards');
    paintDiagram(diagram, rows);
  });

  // Zone 2 — The Domino.
  const zDom = el('section', 'cs-zone cs-zone-domino');
  zDom.appendChild(el('h3', 'cs-zone-h', 'The Domino'));
  renderDomino(zDom, domino);

  zones.append(zDia, zDom);
  host.appendChild(zones);
  host.appendChild(el('hr', 'cs-zone-divider')); // separates the row from Score Analysis below
  paintDiagram(diagram, rows);
}
